import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { es } from 'date-fns/locale';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { GlobalLoadingBar } from '@/components/common/GlobalLoadingBar';
import { NewVersionBanner } from '@/components/common/NewVersionBanner';
import { NotificationsProvider } from '@/components/common/NotificationsProvider';
import { useAuthListener } from '@/features/auth/hooks/useAuthListener';
import { configureAmplify } from '@/lib/amplify';
import { queryClient } from '@/lib/queryClient';
import { AppRouter } from '@/routes';
import { AppRoutes } from '@/routes/AppRoutes';
import { theme } from '@/theme';

// Configure Amplify once at module load, before any render.
configureAmplify();

function AuthInitializer(): null {
  useAuthListener();
  return null;
}

export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <QueryClientProvider client={queryClient}>
            <NotificationsProvider>
              <GlobalLoadingBar />
              <NewVersionBanner />
              <AppRouter>
                <AuthInitializer />
                <AppRoutes />
              </AppRouter>
              {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
            </NotificationsProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
